import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

const CONSOLE_LOGO_URL = 'https://static.wixstatic.com/media/cd2dff_661d95737ba4452d9c15f33d43643f72~mv2.png';

interface PublicInfoLayoutProps {
  children: ReactNode;
}

export default function PublicInfoLayout({ children }: PublicInfoLayoutProps) {
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'About', path: '/about' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Terms of Service', path: '/terms' },
  ];

  const isActive = (path: string) => currentPath === path;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-800 antialiased font-sans">
      {/* Top Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/about" className="flex items-center gap-3 group">
            <img
              src={CONSOLE_LOGO_URL}
              alt="KWSA Logo"
              className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <div className="flex flex-col">
              <span className="font-semibold text-lg leading-tight tracking-tight text-slate-900 group-hover:text-red-700 transition-colors">
                MAPP
              </span>
              <span className="text-xs text-slate-500 font-medium tracking-wide">
                Keller Williams Southern Africa
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 sm:gap-2">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'bg-red-50 text-red-800 font-semibold'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to="/login"
              className="ml-3 inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 transition-colors"
            >
              Sign In
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <Link
              to="/login"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-slate-800"
            >
              Sign In
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="border-b border-slate-200 bg-white px-4 pt-2 pb-4 md:hidden shadow-lg animate-fadeIn">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => {
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`rounded-md px-3 py-2 text-base font-medium ${
                      active
                        ? 'bg-red-50 text-red-800 font-semibold'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md bg-slate-900 px-3 py-2 text-base font-medium text-white text-center mt-2"
              >
                Sign In to MAPP
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 py-10 sm:py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-200 bg-white py-8 text-sm text-slate-500">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col">
              <span className="font-semibold text-slate-800">
                Keller Williams Southern Africa
              </span>
              <p className="text-xs text-slate-500 mt-1 max-w-md">
                Mega Agent Productivity Platform (MAPP) — Operations platform for KWSA associates, leadership, and Market Centres.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm font-medium">
              <Link to="/about" className="text-slate-600 hover:text-red-700 transition-colors">
                About
              </Link>
              <Link to="/privacy" className="text-slate-600 hover:text-red-700 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-600 hover:text-red-700 transition-colors">
                Terms of Service
              </Link>
              <Link to="/login" className="text-slate-600 hover:text-red-700 transition-colors">
                Sign In
              </Link>
            </div>
          </div>

          <div className="mt-6 border-t border-slate-100 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-slate-400">
            <p>© {new Date().getFullYear()} Keller Williams Southern Africa. All rights reserved.</p>
            <p className="mt-1 sm:mt-0">Confidential & Proprietary Operations Platform</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
